import { ReactElement, Ref, Suspense, useEffect } from 'react';
import { Box, Button, LoadingContainer } from '@ff-it/ui';
import { useUser } from 'services';
import type { EditorValue } from 'components/RichText/types';
import * as styles from './CommentStream.css';
import { hasNewer, hasOlder, type CommentState, type Edge } from './commentStore';
import { CommentCard } from './CommentCard';
import { CommentForm } from './CommentForm';
import type { CommentTopic } from './useCommentTopic';
import { useStreamScroll } from './useStreamScroll';
import { useNotificationSeen } from 'modules/notifications';

/** How often a visible stream reconciles against the server. */
const RECONCILIATION_INTERVAL = 60_000;

type CommentListProps = {
  state: CommentState;
  currentUserId: number;
  containerRef: Ref<HTMLDivElement>;
  onScroll: () => void;
  onLoadOlder: () => void;
  onLoadNewer: () => void;
  onRetry: (edge: Edge) => void;
  onDelete: (id: number) => Promise<void>;
};

export function CommentList({
  state,
  currentUserId,
  containerRef,
  onScroll,
  onLoadOlder,
  onLoadNewer,
  onRetry,
  onDelete,
}: CommentListProps): ReactElement {
  const { items, anchor, loading, errors } = state;
  const canLoadOlder = hasOlder(state);
  const canLoadNewer = hasNewer(state) && state.loadedTo !== null;

  return (
    <div className={styles.listContainer} ref={containerRef} onScroll={onScroll}>
      {errors.initial && (
        <Box color="danger" textSize="sm" alignSelf="center">
          Could not load comments.{' '}
          <Button size="xxs" variant="secondary" onClick={() => onRetry('initial')}>
            Retry
          </Button>
        </Box>
      )}
      {canLoadOlder && (
        <Button onClick={onLoadOlder} size="sm" variant="secondary" iconAfter="arrow-up" mx="auto" spin={loading.older}>
          Load older messages
        </Button>
      )}
      {errors.older && (
        <Button onClick={() => onRetry('older')} size="sm" variant="secondary" mx="auto">
          Retry loading older messages
        </Button>
      )}
      {items.length === 0 && !loading.initial && !errors.initial && (
        <Box color="gray500" textSize="sm" alignSelf="center" mt="auto" mb="auto">
          There are no comments yet
        </Box>
      )}
      {items.map((item) => (
        <CommentCard
          key={item.id}
          item={item}
          selected={item.id === anchor}
          canDelete={!item.deleted && item.created_by.id === currentUserId}
          onDelete={onDelete}
        />
      ))}
      {canLoadNewer && (
        <Button
          onClick={onLoadNewer}
          size="sm"
          variant="secondary"
          iconAfter="arrow-down"
          mx="auto"
          spin={loading.newer}
        >
          Load newer messages
        </Button>
      )}
      {errors.newer && (
        <Button onClick={() => onRetry('newer')} size="sm" variant="secondary" mx="auto">
          Retry loading newer messages
        </Button>
      )}
    </div>
  );
}

type CommentStreamProps = {
  topic: CommentTopic;
  commentId: number | null;
  onUnmount?: () => void;
};

export function CommentStream({ topic, commentId, onUnmount }: CommentStreamProps): ReactElement {
  const { store, state } = topic;
  const scroll = useStreamScroll(state);
  const currentUserId = useUser().id;
  useNotificationSeen(topic.objectKey, { comments: true });

  useEffect(() => {
    store.open(commentId);
    return () => store.close();
  }, [store, commentId]);

  useEffect(() => {
    const onFocus = () => store.sync();
    window.addEventListener('focus', onFocus);
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        store.sync();
      }
    }, RECONCILIATION_INTERVAL);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(intervalId);
    };
  }, [store]);

  useEffect(() => () => onUnmount?.(), [onUnmount]);

  const onCreate = async (content: EditorValue): Promise<void> => {
    await store.create(content);
    scroll.scrollToEnd();
  };

  const onLoadOlder = (): void => {
    scroll.captureBeforePrepend();
    void store.loadOlder();
  };

  return (
    <LoadingContainer loading={state.loading.initial === true} className={styles.loaderContainer}>
      <CommentList
        state={state}
        currentUserId={currentUserId}
        containerRef={scroll.containerRef}
        onScroll={scroll.onScroll}
        onLoadOlder={onLoadOlder}
        onLoadNewer={() => void store.loadNewer()}
        onRetry={(edge) => void store.retry(edge)}
        onDelete={store.remove}
      />

      {/* editor is lazy */}
      <Suspense>
        <CommentForm onCreate={onCreate} />
      </Suspense>
    </LoadingContainer>
  );
}
